import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Collapse,
  Alert,
  InputAdornment,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSaveProcedureCatalog } from './hooks/useSaveProcedureCatalog';

export default function ProcedureCatalogFormModal({
  open,
  onClose,
  onSave,
  procedure,
  initialDescription,
}) {
  const navigate = useNavigate();
  const { save: saveProcedureCatalog } = useSaveProcedureCatalog();
  const [formDescription, setFormDescription] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setFormDescription(procedure?.description ?? initialDescription ?? '');
      setFormAmount(procedure?.amount != null ? String(procedure.amount) : '');
      setError('');
    }
  }, [open, procedure, initialDescription]);

  const save = () => {
    if (!formDescription.trim()) return;

    const payload = {
      description: formDescription,
      amount: formAmount !== '' ? parseFloat(formAmount.replace(',', '.')) : null,
    };

    saveProcedureCatalog(procedure ? procedure.id : null, payload)
      .then((returnFromApi) => {
        onSave(returnFromApi.data);
        onClose();
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          navigate('/expired-session');
        } else {
          setError(err.response?.data ?? 'Erro ao salvar procedimento.');
        }
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {procedure ? 'Editar Procedimento' : 'Novo Procedimento'}
      </DialogTitle>
      <DialogContent>
        <Collapse in={!!error} sx={{ mb: 1 }}>
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Collapse>
        <TextField
          autoFocus
          label="Descrição"
          fullWidth
          value={formDescription}
          onChange={(e) => setFormDescription(e.target.value)}
          inputProps={{ 'data-cy': 'catalog-description' }}
          sx={{ mt: 1 }}
        />
        <TextField
          label="Valor Padrão"
          fullWidth
          value={formAmount}
          onChange={(e) => setFormAmount(e.target.value)}
          inputProps={{ inputMode: 'decimal', 'data-cy': 'catalog-amount' }}
          InputProps={{
            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
          }}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!formDescription.trim()}
          data-cy="save-catalog-btn"
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ProcedureCatalogFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  procedure: PropTypes.shape({
    id: PropTypes.number,
    description: PropTypes.string,
    amount: PropTypes.number,
  }),
  initialDescription: PropTypes.string,
};

ProcedureCatalogFormModal.defaultProps = {
  procedure: null,
  initialDescription: '',
};
